import L from 'leaflet';

/*
 * Выделение выбранной линии: под линией рисуется светящаяся "подложка" (halo)
 * той же формы. Цвет и стиль самой линии (цвет по напряжению, пунктир) не меняются.
 * Одновременно выделена только одна линия.
 *
 * Выделение привязано к id линии, а не к конкретному объекту L.Polyline:
 * при сдвиге карты ShowAllVisiblePoles удаляет все линии (UnLoadAllLines) и
 * рисует их заново — новая копия выбранной линии снова получает подсветку.
 */

const HALO_STYLE = {
  color: '#00e5ff',   // голубой — его нет среди цветов линий по напряжению
  weight: 14,
  opacity: 0.55,
  lineCap: 'round',
  lineJoin: 'round',
  interactive: false,
  className: 'line-highlight-halo',
};

// Состояние выделения
let selectedId = null;   // id выбранной линии (null — ничего не выбрано)
let selectedMap = null;
let attached = null;     // { polyline, halo, origSetLatLngs, hadOwnSetLatLngs } — текущая копия на карте
let fallbackLine = null; // если у линии нет id — выделение держится только за сам объект

function injectStyles() {
  if (document.getElementById('line-highlight-styles')) { return; }
  const style = document.createElement('style');
  style.id = 'line-highlight-styles';
  style.textContent = `
    @keyframes line-highlight-pulse {
      0%, 100% { stroke-opacity: 0.55; }
      50%      { stroke-opacity: 0.25; }
    }
    path.line-highlight-halo { animation: line-highlight-pulse 1.6s ease-in-out infinite; }
  `;
  document.head.appendChild(style);
}

function hasId(polyline) {
  return polyline && polyline.options && polyline.options.id !== undefined && polyline.options.id !== null;
}

function isSelected(polyline) {
  if (!polyline) { return false; }
  if (selectedId !== null) { return hasId(polyline) && polyline.options.id === selectedId; }
  return fallbackLine === polyline;
}

/* ---------------- подсветка конкретной копии линии ---------------- */

function attach(polyline) {
  const halo = L.polyline(polyline.getLatLngs(), {
    ...HALO_STYLE,
    pane: polyline.options.pane || 'overlayPane',
  }).addTo(selectedMap);

  // сама линия — поверх подложки
  if (polyline._map) { polyline.bringToFront(); }

  // если меняется геометрия линии (например, перетащили опору) — подложка повторяет её
  const hadOwnSetLatLngs = Object.prototype.hasOwnProperty.call(polyline, 'setLatLngs');
  const origSetLatLngs = polyline.setLatLngs;
  polyline.setLatLngs = function () {
    const result = origSetLatLngs.apply(this, arguments);
    halo.setLatLngs(this.getLatLngs());
    return result;
  };

  polyline.on('remove', onAttachedRemoved);
  attached = { polyline, halo, origSetLatLngs, hadOwnSetLatLngs };
}

function detach() {
  if (!attached) { return; }
  const { polyline, halo, origSetLatLngs, hadOwnSetLatLngs } = attached;
  attached = null;
  halo.remove();
  if (hadOwnSetLatLngs) {
    polyline.setLatLngs = origSetLatLngs;
  } else {
    delete polyline.setLatLngs; // снова берётся метод из прототипа
  }
  polyline.off('remove', onAttachedRemoved);
}

// Копию линии убрали с карты (перезагрузка видимой области, удаление, выгрузка).
// Подложку убираем, но выбор по id сохраняем — вдруг линия сейчас же появится снова.
function onAttachedRemoved() {
  detach();
  if (selectedId === null) {
    // у линии не было id — переносить выделение не на что
    fallbackLine = null;
    stopWatching();
  }
}

// На карту добавлен слой: если это новая копия выбранной линии — подсвечиваем её.
function onLayerAdd(e) {
  if (attached || selectedId === null) { return; }
  const layer = e.layer;
  if (layer instanceof L.Polyline && hasId(layer) && layer.options.id === selectedId) {
    attach(layer);
  }
}

function startWatching() {
  if (selectedMap) { selectedMap.on('layeradd', onLayerAdd); }
}

function stopWatching() {
  if (selectedMap) { selectedMap.off('layeradd', onLayerAdd); }
}

/* ---------------- публичное API ---------------- */

export function getHighlightedLine() {
  return attached ? attached.polyline : null;
}

/**
 * Выделить линию. Предыдущее выделение линии снимается.
 * @param {L.Map} map
 * @param {L.Polyline} polyline
 */
export function highlightLine(map, polyline) {
  if (!polyline || !map) { return; }
  if (isSelected(polyline) && attached && attached.polyline === polyline) { return; }
  clearLineHighlight();
  injectStyles();

  selectedMap = map;
  if (hasId(polyline)) {
    selectedId = polyline.options.id;
  } else {
    fallbackLine = polyline;
  }
  startWatching();
  if (polyline._map) { attach(polyline); }
}

/**
 * Снять выделение. Если передана конкретная линия — снимается только если
 * выбрана именно она (сравнение по id, так что подходит и старая копия линии
 * из замыкания попапа/шторки).
 */
export function clearLineHighlight(specificPolyline = null) {
  if (selectedId === null && !fallbackLine) { return; }
  if (specificPolyline && !isSelected(specificPolyline)) { return; }

  detach();
  stopWatching();
  selectedId = null;
  fallbackLine = null;
  selectedMap = null;
}
